import loc from 'owa-localize';
import { computed } from 'mobx';
import { getAllOptions, OptionWithPath } from 'owa-options-store';
import { ALLOWED_SETTING_SUGGESTIONS_KEYS } from '../../searchConstants';
import * as Fuse from 'fuse.js';

/**
 * Basically the same as an OptionWithPath,
 * but with the addition of 'localizedSearchTerms'
 * property which is used for Fuse's string searching.
 */
interface SettingsSearchEntry {
    localizedSearchTerms: string;
    option: OptionWithPath;
}

/**
 * The brains behind searching OWA settings/options.
 *
 * Handles reaching into the options store to retrieve user-allowed options (see
 * 'owa-allowed-options'), then reduces that list to a subset of options we care about.
 * (For now, as part of 'sea-settingSearchSuggestion', we are limiting the
 * scope of which settings can be suggested to users.)
 *
 * @return Returns a Fuse object which can be called upon to perform a trivial
 * fuzzy search across each setting's search-terms.
 *
 */
const searchModel = computed(() => {
    const searchEntries: SettingsSearchEntry[] = getAllOptions()
        .filter(option => ALLOWED_SETTING_SUGGESTIONS_KEYS.includes(option.key))
        .map(option => ({
            localizedSearchTerms:
                (loc(option.searchTermsStringKey) as string) ||
                (loc(option.titleStringKey) as string) ||
                '',
            option: option,
        }));

    const maxLength = searchEntries.reduce(
        (max, searchEntry) => Math.max(max, searchEntry.localizedSearchTerms.length),
        0
    );

    return new Fuse(searchEntries, {
        shouldSort: true,
        threshold: 0.4,
        location: 0,
        distance: maxLength,
        maxPatternLength: 50,
        tokenize: true,
        keys: ['localizedSearchTerms'],
    });
});

/**
 * Retrieves 'settings' search results.
 *
 * @param  {string} query The query to use during search.
 * @return {OptionWithPath[]} Returns the setting options found from the search.
 */
export default function getSettingsSuggestions(query: string): OptionWithPath[] {
    return searchModel
        .get()
        .search(query)
        .map(settingEntry => settingEntry.option);
}
